/**
 * OverlapVenn Component
 *
 * Compact SVG Venn diagram with two overlapping circles.
 * - Circle area proportional to sample size (r proportional to sqrt(total))
 * - Intersection zone filled with neutral color matching the Shared stat card
 * - Only count + UNIQUE label inside circles; name + total in legend below
 */

interface OverlapVennProps {
  readonly subjectA: { readonly name: string; readonly total: number }
  readonly subjectB: { readonly name: string; readonly total: number }
  readonly overlapCount: number
  readonly percentA: number
  readonly percentB: number
  readonly className?: string
}

function formatCompact(value: number): string {
  const absValue = Math.abs(value)
  if (absValue >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`
  if (absValue >= 1_000) return `${(value / 1_000).toFixed(1)}K`
  return value.toLocaleString()
}

const MAX_R = 90
const MIN_R = 48

function calcRadii(totalA: number, totalB: number): { rA: number; rB: number } {
  const maxTotal = Math.max(totalA, totalB, 1)
  const rawA = MAX_R * Math.sqrt(totalA / maxTotal)
  const rawB = MAX_R * Math.sqrt(totalB / maxTotal)
  return {
    rA: Math.max(rawA, MIN_R),
    rB: Math.max(rawB, MIN_R),
  }
}

export function OverlapVenn({
  subjectA,
  subjectB,
  overlapCount,
  percentA,
  percentB,
  className = '',
}: OverlapVennProps) {
  const uniqueA = subjectA.total - overlapCount
  const uniqueB = subjectB.total - overlapCount
  const { rA, rB } = calcRadii(subjectA.total, subjectB.total)
  const smallerR = Math.min(rA, rB)

  const hasOverlap = overlapCount > 0
  const avgPercent = (percentA + percentB) / 2
  const normalized = Math.min(avgPercent / 50, 1)
  const depthFraction = hasOverlap ? 0.25 + 0.45 * Math.sqrt(normalized) : 0
  const overlapDepth = smallerR * depthFraction

  const centerDist = rA + rB - overlapDepth

  const viewWidth = 480
  const midX = viewWidth / 2
  const cxA = midX - centerDist / 2
  const cxB = midX + centerDist / 2
  const cy = 110
  const maxR = Math.max(rA, rB)

  // Position text offset toward outer edge, away from intersection
  const textAx = hasOverlap ? cxA - rA * 0.3 : cxA
  const textBx = hasOverlap ? cxB + rB * 0.3 : cxB

  // Legend row Y position
  const legendY = cy + maxR + 24
  const vbHeight = legendY + 30

  return (
    <div className={`bg-v11-surface border border-v11-border rounded-lg p-4 ${className}`}>
      <svg
        viewBox={`0 0 ${viewWidth} ${vbHeight}`}
        className="w-full max-w-xl mx-auto"
        aria-label="Venn diagram showing audience overlap"
      >
        <defs>
          <radialGradient id="ov-grad-a" cx="40%" cy="40%">
            <stop offset="0%" stopColor="rgb(59,130,246)" stopOpacity="0.22" />
            <stop offset="100%" stopColor="rgb(59,130,246)" stopOpacity="0.04" />
          </radialGradient>
          <radialGradient id="ov-grad-b" cx="60%" cy="40%">
            <stop offset="0%" stopColor="rgb(168,85,247)" stopOpacity="0.22" />
            <stop offset="100%" stopColor="rgb(168,85,247)" stopOpacity="0.04" />
          </radialGradient>
          <clipPath id="ov-clip-a">
            <circle cx={cxA} cy={cy} r={rA} />
          </clipPath>
        </defs>

        {/* Circle A */}
        <circle
          cx={cxA} cy={cy} r={rA}
          fill="url(#ov-grad-a)"
          stroke="rgb(59,130,246)" strokeWidth="2" strokeOpacity="0.6"
        />

        {/* Circle B */}
        <circle
          cx={cxB} cy={cy} r={rB}
          fill="url(#ov-grad-b)"
          stroke="rgb(168,85,247)" strokeWidth="2" strokeOpacity="0.6"
        />

        {/* Intersection fill — orange for shared zone */}
        {hasOverlap && (
          <circle
            cx={cxB} cy={cy} r={rB}
            clipPath="url(#ov-clip-a)"
            fill="rgb(249,115,22)"
            fillOpacity="0.18"
          />
        )}

        {/* ---- Inside Circle A: count + label ---- */}
        <text
          x={textAx} y={cy - 6}
          textAnchor="middle"
          className="fill-v11-text-primary"
          fontSize="20" fontWeight="700"
        >
          {formatCompact(uniqueA)}
        </text>
        <text
          x={textAx} y={cy + 12}
          textAnchor="middle"
          fill="rgb(59,130,246)"
          fontSize="10" fontWeight="600" letterSpacing="0.5"
        >
          UNIQUE A
        </text>

        {/* ---- Inside Circle B: count + label ---- */}
        <text
          x={textBx} y={cy - 6}
          textAnchor="middle"
          className="fill-v11-text-primary"
          fontSize="20" fontWeight="700"
        >
          {formatCompact(uniqueB)}
        </text>
        <text
          x={textBx} y={cy + 12}
          textAnchor="middle"
          fill="rgb(168,85,247)"
          fontSize="10" fontWeight="600" letterSpacing="0.5"
        >
          UNIQUE B
        </text>

        {/* ---- Legend below: badge + name + total for each subject ---- */}
        {/* Badge A */}
        <rect x={20} y={legendY - 10} width={20} height={20} rx={4} fill="rgba(59,130,246,0.2)" />
        <text
          x={30} y={legendY + 1}
          textAnchor="middle"
          fill="rgb(59,130,246)"
          fontSize="10" fontWeight="700" dominantBaseline="middle"
        >
          A
        </text>
        <text
          x={48} y={legendY + 1}
          className="fill-v11-text-primary"
          fontSize="11" fontWeight="600" dominantBaseline="middle"
        >
          {subjectA.name}
        </text>
        <text
          x={48} y={legendY + 15}
          className="fill-v11-text-muted"
          fontSize="10"
        >
          Total: {formatCompact(subjectA.total)}
        </text>

        {/* Badge B */}
        <rect x={viewWidth - 40} y={legendY - 10} width={20} height={20} rx={4} fill="rgba(168,85,247,0.2)" />
        <text
          x={viewWidth - 30} y={legendY + 1}
          textAnchor="middle"
          fill="rgb(168,85,247)"
          fontSize="10" fontWeight="700" dominantBaseline="middle"
        >
          B
        </text>
        <text
          x={viewWidth - 48} y={legendY + 1}
          textAnchor="end"
          className="fill-v11-text-primary"
          fontSize="11" fontWeight="600" dominantBaseline="middle"
        >
          {subjectB.name}
        </text>
        <text
          x={viewWidth - 48} y={legendY + 15}
          textAnchor="end"
          className="fill-v11-text-muted"
          fontSize="10"
        >
          Total: {formatCompact(subjectB.total)}
        </text>
      </svg>
    </div>
  )
}

export default OverlapVenn
